import { useEffect, useRef, useState, type FormEvent } from "react";

import {
  answerExplanations,
  directionOutcomeList,
  directionOutcomes,
  offerByDirection,
  soulCheckIntro,
  soulCheckMethodCopy,
  soulCheckQuestions,
  siteCopy,
} from "../../content";
import {
  soulCheckAnswersSchema,
  type SoulCheckAnswers,
} from "../../lib/intake-schema";
import {
  computeDirection,
  type DirectionResult,
} from "../../lib/scoring";

import "./soul-check.css";

const copy = siteCopy.soulCheckPanel;

type AnswerCode = SoulCheckAnswers[keyof SoulCheckAnswers];

export interface SoulCheckCompletion {
  answers: SoulCheckAnswers;
  /** Kept in memory only; sharing it requires a separate explicit consent step. */
  reflection: string;
  result: DirectionResult;
}

export interface SoulCheckProps {
  onComplete?: (completion: SoulCheckCompletion) => void;
  onClose?: () => void;
  /** Use inside an existing dialog that already supplies its own h2 and close control. */
  embedded?: boolean;
}

export function SoulCheck({ onComplete, onClose, embedded = false }: SoulCheckProps) {
  const [step, setStep] = useState(0);
  const [reflection, setReflection] = useState("");
  const [answers, setAnswers] = useState<Partial<SoulCheckAnswers>>({});
  const [error, setError] = useState("");
  const [result, setResult] = useState<DirectionResult | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  const question = step > 0 ? soulCheckQuestions[step - 1] : undefined;
  const progressLabel =
    step === 0 ? "Vooraf · optionele reflectie" : `Vraag ${step} van 4`;
  const PanelTitle = embedded ? "h3" : "h2";
  const SectionTitle = embedded ? "h4" : "h3";
  const DirectionTitle = embedded ? "h5" : "h4";

  useEffect(() => {
    headingRef.current?.focus();
  }, [result, step]);

  function chooseAnswer(questionId: keyof SoulCheckAnswers, code: AnswerCode) {
    setAnswers((current) => ({ ...current, [questionId]: code }));
    setError("");
  }

  function goBack() {
    setError("");
    setStep((current) => Math.max(0, current - 1));
  }

  function completeCheck() {
    const parsed = soulCheckAnswersSchema.safeParse(answers);
    if (!parsed.success) {
      setError("Er ontbreekt nog een antwoord. Kijk de vier vragen even na.");
      return;
    }

    const nextResult = computeDirection(parsed.data);
    setResult(nextResult);
    setError("");
    onComplete?.({
      answers: parsed.data,
      reflection: reflection.trim(),
      result: nextResult,
    });
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (step === 0) {
      setStep(1);
      return;
    }

    if (!question || answers[question.id] === undefined) {
      setError("Kies het antwoord dat nu het dichtst bij je ligt.");
      return;
    }

    if (step < soulCheckQuestions.length) {
      setStep((current) => current + 1);
      setError("");
      return;
    }

    completeCheck();
  }

  function restart() {
    setStep(0);
    setReflection("");
    setAnswers({});
    setError("");
    setResult(null);
  }

  if (result) {
    const outcome = directionOutcomes[result.direction];
    const offer = offerByDirection[result.direction];

    return (
      <section
        className="soul-check soul-check--result"
        data-embedded={embedded || undefined}
        data-step="result"
        aria-labelledby="soul-check-result-title"
      >
        <header className="soul-check__header">
          <div>
            <p className="soul-check__eyebrow">{copy.resultEyebrow}</p>
            <PanelTitle
              className="soul-check__title"
              id="soul-check-result-title"
              ref={headingRef}
              tabIndex={-1}
            >
              {outcome.label}
            </PanelTitle>
          </div>
          {onClose && !embedded ? (
            <button className="soul-check__quiet-button" type="button" onClick={onClose}>
              Terug naar de Shack
            </button>
          ) : null}
        </header>

        <div className="soul-check__result-intro" aria-live="polite">
          <span aria-hidden="true">≈</span>
          <div>
            <SectionTitle>{outcome.headline}</SectionTitle>
            <p>{outcome.explanation}</p>
            <p>{outcome.invitation}</p>
          </div>
        </div>

        <section className="soul-check__why" aria-labelledby="soul-check-why-title">
          <SectionTitle id="soul-check-why-title">{copy.whyTitle}</SectionTitle>
          <ul>
            {result.contributions.map((contribution) => (
              <li key={contribution.question}>
                {answerExplanations[contribution.answer]}
              </li>
            ))}
          </ul>
          <details>
            <summary>{copy.methodSummary}</summary>
            <p>{soulCheckMethodCopy}</p>
          </details>
        </section>

        <section className="soul-check__directions" aria-labelledby="soul-check-directions-title">
          <SectionTitle id="soul-check-directions-title">{copy.directionsTitle}</SectionTitle>
          <p>{copy.directionsIntro}</p>
          <div className="soul-check__direction-grid">
            {directionOutcomeList.map((item) => {
              const isRecommended = item.direction === result.direction;
              return (
                <article
                  className="soul-check__direction"
                  data-recommended={isRecommended || undefined}
                  key={item.direction}
                >
                  {isRecommended ? (
                    <p className="soul-check__match">{copy.matchLabel}</p>
                  ) : null}
                  <DirectionTitle>{item.label}</DirectionTitle>
                  <p>{item.description}</p>
                  <small>{item.roomResponse}</small>
                </article>
              );
            })}
          </div>
        </section>

        <div className="soul-check__actions soul-check__actions--result">
          <a className="soul-check__primary-link" href={offer.href} target="_blank" rel="noreferrer">
            {offer.ctaLabel}
            <span className="soul-check__external" aria-hidden="true">↗</span>
          </a>
          <button
            className="soul-check__secondary-button"
            type="button"
            onClick={() => {
              setResult(null);
              setStep(4);
            }}
          >
            Laatste antwoord aanpassen
          </button>
          <button className="soul-check__text-button" type="button" onClick={restart}>
            Hele check opnieuw doen
          </button>
        </div>
      </section>
    );
  }

  return (
    <section
      className="soul-check"
      data-embedded={embedded || undefined}
      data-step={step === 0 ? "intro" : "question"}
      aria-labelledby="soul-check-title"
    >
      <header className="soul-check__header">
        <div>
          <p className="soul-check__eyebrow">
            {step === 0 ? soulCheckIntro.eyebrow : question?.eyebrow}
          </p>
          <PanelTitle
            className="soul-check__title"
            id="soul-check-title"
            ref={headingRef}
            tabIndex={-1}
          >
            {step === 0 ? soulCheckIntro.title : question?.title}
          </PanelTitle>
        </div>
        {onClose && !embedded ? (
          <button className="soul-check__quiet-button" type="button" onClick={onClose}>
            Sluiten
          </button>
        ) : null}
      </header>

      <div className="soul-check__progress-wrap">
        <div className="soul-check__progress-label" aria-live="polite">
          <span>{progressLabel}</span>
          <span>{step}/4</span>
        </div>
        <progress aria-label={progressLabel} max={4} value={step} />
      </div>

      <form className="soul-check__form" onSubmit={handleSubmit} noValidate>
        {step === 0 ? (
          <div className="soul-check__reflection">
            <p className="soul-check__intro">{soulCheckIntro.description}</p>
            <label htmlFor="soul-check-reflection">
              {soulCheckIntro.reflectionPrompt}
              <span>{copy.reflectionOptionalLabel}</span>
            </label>
            <textarea
              id="soul-check-reflection"
              maxLength={1_200}
              onChange={(event) => setReflection(event.target.value)}
              placeholder={copy.reflectionPlaceholder}
              rows={embedded ? 4 : 6}
              value={reflection}
            />
            <div className="soul-check__field-meta">
              <p>{soulCheckIntro.privacy}</p>
              <span>{reflection.length}/1200</span>
            </div>
          </div>
        ) : question ? (
          <fieldset className="soul-check__question" aria-describedby={error ? "soul-check-error" : undefined}>
            <legend>{question.prompt}</legend>
            <div className="soul-check__options">
              {question.options.map((option) => {
                const inputId = `soul-check-${question.id}-${option.code}`;
                return (
                  <label className="soul-check__option" htmlFor={inputId} key={option.code}>
                    <input
                      checked={answers[question.id] === option.code}
                      id={inputId}
                      name={question.id}
                      onChange={() => chooseAnswer(question.id, option.code)}
                      type="radio"
                      value={option.code}
                    />
                    <span aria-hidden="true" className="soul-check__radio-mark" />
                    <span>{option.label}</span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        ) : null}

        <p className="soul-check__error" id="soul-check-error" role="alert">
          {error}
        </p>

        <div className="soul-check__actions">
          {step > 0 ? (
            <button className="soul-check__secondary-button" type="button" onClick={goBack}>
              Terug
            </button>
          ) : null}
          <button className="soul-check__primary-button" type="submit">
            {step === 0
              ? "Begin de vier vragen"
              : step === soulCheckQuestions.length
                ? "Bekijk mijn richting"
                : "Volgende vraag"}
          </button>
        </div>
      </form>
    </section>
  );
}
